// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { parseLocalDate } from '@/utils/formatters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, Clock, FileVideo, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VideoData {
  id: string;
  file_path: string;
  file_name: string;
  file_size: number;
  upload_date: string;
  expiry_date: string;
  is_available: boolean;
  order: {
    player_name: string;
    purchaser_name: string;
    purchaser_email: string;
  };
}

export const VideoDownload: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (orderId) {
      fetchVideoData();
    }
  }, [orderId]);

  const fetchVideoData = async () => {
    try {
      const { data, error } = await supabase
        .from('completed_videos')
        .select(`
          *,
          order:video_orders(
            player_name,
            purchaser_name,
            purchaser_email
          )
        `)
        .eq('order_id', orderId)
        .single();

      if (error) throw error;

      setVideoData(data);
    } catch (error) {
      console.error('Failed to fetch video data:', error);
      setError('Video not found or access denied');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!videoData) return;

    setDownloading(true);
    try {
      // Create a signed URL for download (1 hour expiry)
      const { data, error } = await supabase.storage
        .from('completed-videos')
        .createSignedUrl(videoData.file_path, 3600); // 1 hour

      if (error) throw error;

      // Create a temporary link and click it to download
      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = videoData.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Download started!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download video');
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return parseLocalDate(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (): number => {
    if (!videoData) return 0;
    const now = new Date();
    const expiry = new Date(videoData.expiry_date);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const isExpired = (): boolean => {
    if (!videoData) return true;
    return new Date() > new Date(videoData.expiry_date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading video details...</p>
        </div>
      </div>
    );
  }

  if (error || !videoData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-center text-destructive">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
              Video Not Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              {error || 'The video you are looking for could not be found or has expired.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining();
  const expired = isExpired() || !videoData.is_available;

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Video Download
          </h1>
          <p className="text-muted-foreground">
            Download your completed video
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileVideo className="h-5 w-5" />
              {videoData.order.player_name} - Highlight Video
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              For: {videoData.order.purchaser_name}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">File Name:</span>
                <p className="text-muted-foreground">{videoData.file_name}</p>
              </div>
              <div>
                <span className="font-medium">File Size:</span>
                <p className="text-muted-foreground">{formatFileSize(videoData.file_size)}</p>
              </div>
              <div>
                <span className="font-medium">Upload Date:</span>
                <p className="text-muted-foreground">{formatDate(videoData.upload_date)}</p>
              </div>
              <div>
                <span className="font-medium">Expires:</span>
                <p className="text-muted-foreground">{formatDate(videoData.expiry_date)}</p>
              </div>
            </div>

            {!expired && (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  This video will be available for download for {daysRemaining} more day{daysRemaining !== 1 ? 's' : ''}.
                </AlertDescription>
              </Alert>
            )}

            {expired && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This video has expired and is no longer available for download.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-center">
              <Button
                onClick={handleDownload}
                disabled={downloading || expired}
                size="lg"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                {downloading ? 'Preparing Download...' : 'Download Video'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Important Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>• Videos are available for download for 7 days from the upload date</p>
            <p>• After expiration, videos will be automatically deleted from our servers</p>
            <p>• For questions or support, please contact our team</p>
            <p>• Make sure to save the video to your device before it expires</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};